import React, { memo, useMemo, useCallback } from 'react';
import { Checkbox } from 'antd';
import { ChatMessage } from './ChatMessage';
import { resolveMessageSenderAvatarUrl } from '@utils/chatAvatar';

function getMessageText(msg) {
    if (!msg) return '';
    if (msg.content?.text) return msg.content.text;
    if (typeof msg.content === 'string') return msg.content;
    return msg.body ?? msg.message ?? '';
}

function getInitials(name) {
    if (!name) return '?';
    return name
        .split(/\s+/)
        .map((s) => s[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
}

function formatMessageTime(createdAt) {
    if (!createdAt) return '';
    const d = new Date(createdAt);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function getSenderDisplayName(msg, currentUserId, currentUserName, participants = []) {
    const senderId = msg.senderId ?? msg.userId ?? msg.createdBy;
    const own = String(senderId) === String(currentUserId);
    if (own) return currentUserName || 'You';
    const fromMsg = msg.senderName ?? msg.senderEmail ?? msg.userName ?? msg.sender?.name ?? msg.sender?.email;
    if (fromMsg) return fromMsg;
    const participant = participants.find((p) => String(p.userId ?? p.id) === String(senderId));
    return participant?.name ?? participant?.email ?? 'Unknown';
}

export const ChatMessageRow = memo(function ChatMessageRow({
    msg,
    idxKey,
    participants,
    currentUserId,
    currentUserName,
    dark,
    selectionMode,
    isSelected,
    reactions: reactionsProp,
    onSelectToggle,
    onSelectClick,
    onReplyClick,
    onAddReaction,
    onToggleReaction,
    onEditClick,
    onDeleteClick,
    onForwardSelect,
    addReactionLoading,
    threadCountByParent,
    threadCountOverlay,
    /** When true (e.g. thread drawer), hide "Reply in thread" — already in a thread. */
    disableThreadReply = false,
}) {
    const msgId = msg?.id ?? msg?._id ?? idxKey;
    const senderId = msg?.senderId ?? msg?.userId ?? msg?.createdBy;
    const isOwn = String(senderId) === String(currentUserId);

    const senderName = useMemo(
        () => getSenderDisplayName(msg, currentUserId, currentUserName, participants),
        [msg, currentUserId, currentUserName, participants],
    );

    const senderAvatarImageUrl = useMemo(
        () => resolveMessageSenderAvatarUrl(msg, participants),
        [msg, participants],
    );

    const threadInfo = threadCountByParent?.[msgId];
    const overlay = threadCountOverlay?.[msgId];
    const serverReplyN = msg?.replyCount ?? msg?.reply_count;
    const overlayCount = overlay?.count;
    const derivedCount = threadInfo?.count;
    /** Use max so local/socket bumps (overlay, derived from loaded replies) beat stale server replyCount. */
    const replyCandidates = [serverReplyN, overlayCount, derivedCount].filter(
        (x) => typeof x === 'number' && !Number.isNaN(x),
    );
    const replyCount = replyCandidates.length > 0 ? Math.max(0, ...replyCandidates) : 0;
    const lastReplyAt =
        overlay?.lastReplyAt ??
        threadInfo?.lastReplyAt ??
        msg?.lastReplyAt ??
        msg?.last_reply_at ??
        null;

    const attachments = msg?.content?.attachments ?? msg?.attachments ?? [];
    const messagePreviewImages =
        msg?.previewImages ?? msg?.preview_images ?? msg?.content?.previewImages ?? msg?.content?.preview_images;

    const reactions = useMemo(
        () => reactionsProp ?? msg?.reactions ?? msg?.reactionCounts ?? [],
        [reactionsProp, msg],
    );

    const myReactionEmoji = useMemo(() => {
        const direct = msg?.myReactionEmoji ?? null;
        if (direct) return direct;
        if (!Array.isArray(reactions)) return null;
        const mine = reactions.find((r) =>
            Array.isArray(r?.userIds)
                ? r.userIds.some((id) => String(id) === String(currentUserId))
                : Array.isArray(r?.users)
                    ? r.users.some((u) => String(u.userId ?? u.id) === String(currentUserId))
                    : String(r.userId ?? r.id) === String(currentUserId),
        );
        return mine?.emoji ?? null;
    }, [msg, reactions, currentUserId]);

    const edited = useMemo(() => {
        // Backend can express edits in multiple ways. Prefer explicit flags/fields if present.
        if (msg?.edited === true || msg?.isEdited === true) return true;
        const editedAt = msg?.editedAt ?? msg?.edited_at ?? null;
        if (editedAt) return true;
        const u = msg?.updatedAt ?? msg?.updated_at ?? null;
        const c = msg?.createdAt ?? msg?.created_at ?? null;
        if (!u || !c) return false;
        const ut = new Date(u).getTime();
        const ct = new Date(c).getTime();
        if (Number.isNaN(ut) || Number.isNaN(ct)) return String(u) !== String(c);
        return ut > ct;
    }, [msg]);

    const onRowClick = useCallback(
        (e) => {
            if (!selectionMode) return;
            onSelectClick?.(e, msgId);
        },
        [selectionMode, onSelectClick, msgId],
    );

    const onCheckboxToggle = useCallback(() => {
        onSelectToggle?.(msgId);
    }, [onSelectToggle, msgId]);

    return (
        <div
            key={msgId}
            data-chat-message-id={String(msgId)}
            onClick={onRowClick}
            style={{
                borderRadius: 10,
                outline: 'none',
                outlineOffset: 0,
                position: 'relative',
                userSelect: 'text',
                WebkitUserSelect: 'text',
            }}
        >
            {selectionMode ? (
                <div
                    style={{
                        position: 'absolute',
                        top: 8,
                        left: isOwn ? 'auto' : 8,
                        right: isOwn ? 8 : 'auto',
                        zIndex: 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 26,
                        height: 26,
                        borderRadius: 999,
                        background: dark ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.9)',
                        backdropFilter: 'blur(6px)',
                        border: dark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.08)',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Checkbox checked={isSelected} onChange={onCheckboxToggle} />
                </div>
            ) : null}

            <ChatMessage
                messageId={msgId}
                userId={senderId}
                senderName={senderName}
                time={formatMessageTime(msg?.createdAt ?? msg?.created_at)}
                body={getMessageText(msg)}
                attachments={attachments}
                messagePreviewImages={messagePreviewImages}
                messageType={msg?.type}
                initials={getInitials(senderName)}
                avatarImageUrl={senderAvatarImageUrl}
                isOwn={isOwn}
                reactions={reactions}
                replyCount={replyCount}
                lastReplyAt={lastReplyAt}
                onReply={
                    disableThreadReply || !onReplyClick ? undefined : () => onReplyClick(msgId)
                }
                onAddReaction={onAddReaction}
                onToggleReaction={onToggleReaction}
                onEdit={onEditClick && isOwn ? () => onEditClick(msg) : undefined}
                onDelete={onDeleteClick && isOwn ? () => onDeleteClick(msgId) : undefined}
                onForwardSelect={onForwardSelect ? () => onForwardSelect(msgId) : undefined}
                addReactionLoading={addReactionLoading}
                currentUserId={currentUserId}
                isSelected={isSelected}
                myReactionEmoji={myReactionEmoji}
                selectionMode={selectionMode}
                edited={edited}
            />
        </div>
    );
});

